import { Button, Icon, Modal, Spinner } from "@thetreep/ui";
import { useTranslation } from "react-i18next";
import { DAP, DAPStatus } from "@gen/dap/v1/dap_pb";
import { useState, useEffect } from "react";
import { useSearchParams } from "react-router";
import { DAPNumber } from "@pages/booking/DAPNumber";
import { Conditions } from "@pages/booking/Conditions";
import { HomeInfos } from "./HomeInfos";
import FAQ from "./FAQ";
import { useUserStore } from "@stores/userStore";
import { routes } from "@routes";
import { useNavigate, useLocation } from "react-router";
import { useModal } from "@hooks/useModal";
import { Registration } from "@pages/authentication/Registration";
import { Login } from "@pages/authentication/Login";
import findDap from "@assets/images/finddap.png";
import { useFetchDap } from "@hooks/useFetchDap";

export function Home() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const { user, isInitialized } = useUserStore();
  const { search } = useLocation();
  const {
    isOpen: isOpenAuth,
    openModal: openModalAuth,
    closeModal: closeModalAuth,
  } = useModal();
  const {
    isOpen: isOpenFindDap,
    openModal: openModalFindDap,
    closeModal: closeModalFindDap,
  } = useModal();
  const [currentStep, setCurrentStep] = useState<
    "account" | "number" | "journey"
  >("account");
  const navigate = useNavigate();
  const [modalType, setModalType] = useState<"login" | "registration">(
    "registration",
  );

  const { dap, isLoading, errorDapMessage, fetchDap } = useFetchDap();

  function canBook(dap: DAP): boolean {
    return (
      !!dap &&
      dap.status == DAPStatus.DAP_STATUS_VALID &&
      dap.isBookableByCaller &&
      !!dap.maxJourneys &&
      dap.journeysBooked < dap.maxJourneys
    );
  }

  useEffect(() => {
    fetchDap(searchParams.get("dap"), searchParams.get("dap_name"));
  }, [searchParams]);

  useEffect(() => {
    if (user && dap) {
      // oxlint-disable-next-line react-hooks/set-state-in-effect
      setCurrentStep("journey");
    } else if (user) {
      setCurrentStep("number");
      closeModalAuth();
    } else {
      setCurrentStep("account");
    }
  }, [user, dap]);

  return (
    <div className={`w-full flex flex-col justify-center `}>
      <div className="w-full max-w-[936px] mx-auto px-5 lg:px-0 py-8 lg:py-16 flex flex-col gap-10 ">
        <div className="flex flex-col gap-4">
          <h1 className="text-[32px] lg:text-[40px] font-jakarta text-blue-medium">
            {t("home.serviceTitle")}
          </h1>
          <p className="text-text-strong text-[14px] lg:text-[24px] font-jakarta">
            {t("home.serviceDescription")}
          </p>
        </div>
        <div
          className={`home-step flex flex-col gap-6 pb-10 border-b border-grey-blue-300 ${
            currentStep === "account" ? "isActive" : ""
          }`}
        >
          <h2 className=" flex flex-col  lg:flex-row lg:items-center gap-2">
            <div className="flex items-center gap-2 mr-2">
              <div className="square flex items-center justify-center w-[32px] h-[32px] rounded-[4px] bg-light-blue-200 text-grey-blue-500 font-bold text-base ">
                1
              </div>
              <span className="title text-blue-strong text-[16px] lg:text-[24px] font-jakarta">
                {t("home.stepCreateAccount")}
              </span>
            </div>

            {user && (
              <div className="flex text-success-strong items-center text-sm font-bold gap-1 ">
                <Icon size={16} src="check_circle_fill" color="#008575" />
                {t("home.stepConnected")}
              </div>
            )}
          </h2>

          {!isInitialized ? (
            <Spinner />
          ) : (
            !user && (
              <>
                <p className="text-text-strong text-[14px] lg:text-[16px]">
                  {t("home.stepDescription1")}
                </p>
                <Button
                  alignself="start"
                  size="compact"
                  onClick={openModalAuth}
                  endEnhancer={<Icon src="arrow_right_line" color="white" />}
                >
                  {t("home.createAccountBtn")}
                </Button>
              </>
            )
          )}
        </div>

        <div
          className={`home-step flex flex-col gap-6 pb-10 border-b border-grey-blue-300 ${
            currentStep === "number" ? "isActive" : ""
          }`}
        >
          <h2 className=" flex flex-col  lg:flex-row lg:items-center gap-2">
            <div className="flex items-center gap-2 mr-2">
              <div className="square flex items-center justify-center w-[32px] h-[32px] rounded-[4px] bg-light-blue-200 text-grey-blue-500 font-bold text-base ">
                2
              </div>
              <span className="title text-blue-strong text-[16px] lg:text-[24px] font-jakarta">
                {t("home.stepAuthNumber")}
              </span>
            </div>

            {dap && (
              <div className="flex text-success-strong items-center text-sm font-bold gap-1 ">
                <Icon size={16} src="check_circle_fill" color="#008575" />
                {t("home.numberValidated")}
              </div>
            )}
          </h2>

          {currentStep !== "account" && (
            <>
              <div className="flex flex-col gap-2">
                <p className="text-text-strong text-[14px] lg:text-[16px]">
                  {t("home.stepDescription2")}
                </p>
                <p
                  onClick={openModalFindDap}
                  className="cursor-pointer underline text-blue-medium text-[14px] lg:text-[16px]"
                >
                  {t("home.findAuthNumberLink")}
                </p>
              </div>

              <Modal
                size="large"
                isOpen={isOpenFindDap}
                onClose={closeModalFindDap}
              >
                <div className="flex flex-col gap-4">
                  <div className="flex flex-col gap-4">
                    <h2 className="text-text-strong text-[20px] font-jakarta font-bold">
                      {t("home.findDapModalTitle")}
                    </h2>
                    <p className="c text-text-strong text-[14px] lg:text-[16px]">
                      {t("home.findDapModalDescription")}
                    </p>
                    <img
                      className="w-full mx-auto max-w-[400px]"
                      src={findDap}
                      alt={t("home.findDapModalImageAlt")}
                    />
                  </div>
                  <Button
                    size="compact"
                    alignself="end"
                    onClick={closeModalFindDap}
                  >
                    {t("modal.close")}
                  </Button>
                </div>
              </Modal>

              <div className="w-full  [&.input-group]:flex [&_.input-group]:max-w-[450px] flex flex-col gap-4">
                <DAPNumber
                  dap={dap}
                  isLoading={isLoading}
                  errorMessage={errorDapMessage}
                />
              </div>
            </>
          )}
        </div>

        <div
          className={`home-step flex flex-col gap-6  ${
            currentStep === "journey" ? "isActive" : ""
          }`}
        >
          <h2 className=" flex flex-col  lg:flex-row lg:items-center gap-2">
            <div className="flex items-center gap-2 mr-2">
              <div className="square flex items-center justify-center w-[32px] h-[32px] rounded-[4px] bg-light-blue-200 text-grey-blue-500 font-bold text-base ">
                3
              </div>
              <span className="title text-blue-strong text-[16px] lg:text-[24px] font-jakarta">
                {t("home.stepJourneyDetails")}
              </span>
            </div>
          </h2>
          {dap && (
            <>
              <p className="text-text-strong text-[14px] lg:text-[16px]">
                {t("home.journeyConditionsIntro")}
              </p>
              <Conditions kind="home" dap={dap} />
            </>
          )}
        </div>

        {currentStep === "journey" && dap && canBook(dap) && (
          <Button
            size="compact"
            alignself="start"
            onClick={() => navigate(`${routes.SEARCH}${search}`)}
            endEnhancer={<Icon src="arrow_right_line" color="white" />}
          >
            {t("home.bookJourneyBtn")}
          </Button>
        )}

        <Modal isOpen={isOpenAuth} onClose={closeModalAuth} size="large">
          <div className="modal-auth">
            {modalType === "registration" ? (
              <Registration onSwitch={() => setModalType("login")} />
            ) : (
              <Login onSwitch={() => setModalType("registration")} />
            )}
          </div>
        </Modal>
      </div>
      <HomeInfos />
      <FAQ />
    </div>
  );
}
